/**
 * Follow the language of the framed knowledge base (REQ-028).
 *
 * On the support wrapper page the KB is a CROSS-ORIGIN iframe, so the wrapper
 * cannot read its URL or `<html lang>`. A Freshdesk language switch is a full
 * page load of that iframe, and the portal head carries a snippet
 * (`infrastructure/surface-injection/freshdesk-kb-locale-snippet.html`) that runs
 * on every load and posts:
 *
 *   { source: 'bln-kb', v: 1, type: 'LOCALE', payload: { locale } }
 *
 * to the wrapper. This module is the receiving end. It trusts nothing it has not
 * pinned, exactly like `parseWidgetMessage`: the exact KB origin, the KB
 * iframe's own `contentWindow`, the envelope, and a locale the tenant supports.
 *
 * Kept separate from the widget protocol (`messages.ts`) on purpose: that
 * channel is between our own two frames; this one crosses into a page we do not
 * control, so it gets its own namespace and is never confused with `AUTH`.
 */

export const KB_MESSAGE_SOURCE = 'bln-kb' as const;
export const KB_MESSAGE_VERSION = 1 as const;

const LOCALE_SHAPE = /^[a-z]{2}(-[a-z]{2})?$/;

/**
 * Validate a locale against the tenant's languages. Mirrors the wrapper route's
 * `resolveLocale`: `xx` or `xx-yy`, accepted when its primary subtag is one the
 * tenant lists (or the tenant lists none). Returns the lower-cased locale, or
 * `null` to drop the message.
 */
export function acceptKbLocale(raw: unknown, languages: readonly string[]): string | null {
  if (typeof raw !== 'string') {
    return null;
  }
  const locale = raw.toLowerCase();
  if (!LOCALE_SHAPE.test(locale)) {
    return null;
  }
  const base = locale.split('-')[0] ?? '';
  if (languages.length > 0 && !languages.includes(base)) {
    return null;
  }
  return locale;
}

/**
 * The fail-closed validator. Returns the accepted locale, or `null` unless the
 * event comes from EXACTLY `kbOrigin` AND from the KB iframe's window AND carries
 * a well-formed `LOCALE` envelope with a supported locale. Never throws.
 */
export function parseKbLocaleMessage(
  event: MessageEvent,
  pin: { origin: string; source: Window | null },
  languages: readonly string[],
): string | null {
  if (pin.origin.length === 0 || event.origin !== pin.origin) {
    return null;
  }
  if (pin.source === null || event.source !== pin.source) {
    return null;
  }
  const data: unknown = event.data;
  if (typeof data !== 'object' || data === null) {
    return null;
  }
  const d = data as Record<string, unknown>;
  if (d.source !== KB_MESSAGE_SOURCE || d.v !== KB_MESSAGE_VERSION || d.type !== 'LOCALE') {
    return null;
  }
  const payload = d.payload;
  if (typeof payload !== 'object' || payload === null) {
    return null;
  }
  return acceptKbLocale((payload as Record<string, unknown>).locale, languages);
}

export interface FollowKbLocaleOptions {
  /** The KB's exact origin, e.g. `https://bluedrop.support.bluedrop360.com`. */
  kbOrigin: string;
  /** The tenant's supported languages (primary subtags). Empty = accept any. */
  languages: readonly string[];
  /** The locale the page opened with — a message repeating it is not a change. */
  locale: string;
  /** Called once per CHANGE of locale, never for a repeat. */
  onChange: (locale: string) => void;
}

/**
 * Listen for the framed KB's language and report each change. Returns an
 * unsubscribe. The KB posts on EVERY page load (including plain navigation in
 * one language), so repeats are swallowed here rather than re-rendering.
 */
export function followKbLocale(
  iframe: HTMLIFrameElement,
  options: FollowKbLocaleOptions,
): () => void {
  let current = options.locale.toLowerCase();
  function onMessage(event: MessageEvent): void {
    // Read contentWindow per message: it is stable across the iframe's own
    // navigations, but resolving it late keeps this correct if the element is
    // replaced.
    const locale = parseKbLocaleMessage(
      event,
      { origin: options.kbOrigin, source: iframe.contentWindow },
      options.languages,
    );
    if (locale === null || locale === current) {
      return;
    }
    current = locale;
    options.onChange(locale);
  }
  window.addEventListener('message', onMessage);
  return () => window.removeEventListener('message', onMessage);
}
